/**
 * What the recorded mouth looks like at a moment of the audio (docs/NEW-UI/09): which mouth, the one it is leaving,
 * and how far the change has gone. Pure: the player asks it once per drawn frame with the audio clock.
 *
 * - Between two mouths a short cross-fade (`blendMs`), the length of one or two plate frames: a cut every 40 ms
 *   flickers, a long fade blurs the mouth.
 * - In and out of speech the same as the MuseTalk service (avatar/run_avatar.sh: 3 frames in, 5 out), from and to
 *   the plate's own mouth (`plate`), so the seam is the one people are used to.
 */
import { FRAME_MS, type Mouth } from './visemes';

/** What to draw over the plate frame: `to` over `from` at `mix` (0..1); `from: 'plate'` means the plate's own mouth. */
export interface MouthState {
  from: Mouth | 'plate';
  to: Mouth | 'plate';
  mix: number;
}

export interface TimelineOptions {
  blendMs?: number;
  rampInMs?: number;
  rampOutMs?: number;
}

const DEFAULTS: Required<TimelineOptions> = { blendMs: 60, rampInMs: 3 * FRAME_MS, rampOutMs: 5 * FRAME_MS };

const PLATE: MouthState = { from: 'plate', to: 'plate', mix: 1 };

/** The mouth at `ms` into a clause whose track is `track` (one mouth per FRAME_MS); the plate outside it. */
export function mouthAt(track: readonly Mouth[], ms: number, options: TimelineOptions = {}): MouthState {
  const o = { ...DEFAULTS, ...options };
  const end = track.length * FRAME_MS;
  if (track.length === 0 || ms < 0 || ms >= end + o.rampOutMs) return PLATE;
  if (ms >= end) return { from: track[track.length - 1]!, to: 'plate', mix: smoothstep((ms - end) / o.rampOutMs) };
  const k = Math.floor(ms / FRAME_MS);
  const to = track[k]!;
  if (ms < o.rampInMs) return { from: 'plate', to, mix: smoothstep((ms + FRAME_MS) / (o.rampInMs + FRAME_MS)) };
  // the fade runs from where this mouth began, so one longer than a frame goes on into the next
  let start = k;
  while (start > 0 && track[start - 1] === to) start--;
  if (start === 0) return { from: to, to, mix: 1 };
  return { from: track[start - 1]!, to, mix: smoothstep((ms - start * FRAME_MS) / o.blendMs) };
}

function smoothstep(x: number): number {
  const t = Math.min(1, Math.max(0, x));
  return t * t * (3 - 2 * t);
}
